import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { reportsAPI } from '../lib/api';
import api from '../lib/api';
import { formatCurrency, formatNumber } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ChartLine, CalendarBlank, Package, TrendUp, TrendDown, Download, FilePdf, Fire } from '@phosphor-icons/react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

export default function Reports() {
  const [dailyData, setDailyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [topIngredients, setTopIngredients] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [productUsage, setProductUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [exporting, setExporting] = useState(null);
  const [usageStartDate, setUsageStartDate] = useState('');
  const [usageEndDate, setUsageEndDate] = useState('');
  
  // Aylık Maliyet Raporu için state
  const [monthlyCostReport, setMonthlyCostReport] = useState(null);
  const [costReportMonth, setCostReportMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [loadingCostReport, setLoadingCostReport] = useState(false);

  useEffect(() => {
    fetchData();
  }, [period]);

  useEffect(() => {
    fetchMonthlyCostReport();
  }, [costReportMonth]);

  const fetchMonthlyCostReport = async () => {
    setLoadingCostReport(true);
    try {
      // Ay başı ve sonu tarihlerini hesapla
      const [year, month] = costReportMonth.split('-');
      const startDate = `${costReportMonth}-01`;
      const endDate = new Date(year, month, 0).toISOString().split('T')[0]; // Ayın son günü
      
      // Gider ve sevkiyat verilerini paralel çek
      const [expensesRes, transfersRes] = await Promise.all([
        api.get(`/expenses?month=${costReportMonth}`),
        api.get(`/reports/transfer-cost?start_date=${startDate}&end_date=${endDate}`)
      ]);
      
      // Giderleri kategorilere göre grupla
      const expenses = expensesRes.data;
      const transfers = transfersRes.data;
      
      // Kategori bazlı gider özeti
      const expenseByCategory = {};
      let totalExpenses = 0;
      expenses.forEach(exp => {
        const cat = exp.category_label || exp.category;
        if (!expenseByCategory[cat]) {
          expenseByCategory[cat] = { amount: 0, count: 0 };
        }
        expenseByCategory[cat].amount += exp.amount;
        expenseByCategory[cat].count += 1;
        totalExpenses += exp.amount;
      });
      
      const transferCost = transfers.summary?.total_cost || 0;
      const grandTotal = totalExpenses + transferCost;
      
      setMonthlyCostReport({
        month: costReportMonth,
        expenses: expenseByCategory,
        totalExpenses,
        transferCost,
        transferCount: transfers.summary?.total_transfers || 0,
        grandTotal,
        categoryBreakdown: transfers.category_summary || []
      });
    } catch (error) {
      toast.error('Aylık maliyet raporu yüklenirken hata oluştu');
    } finally {
      setLoadingCostReport(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dailyRes, monthlyRes, ingredientsRes, lowStockRes] = await Promise.all([
        reportsAPI.getDaily(parseInt(period)),
        reportsAPI.getMonthly(12),
        reportsAPI.getTopIngredients(parseInt(period), 10),
        reportsAPI.getLowStock(),
      ]);
      
      setDailyData(dailyRes.data);
      setMonthlyData(monthlyRes.data);
      setTopIngredients(ingredientsRes.data);
      setLowStock(lowStockRes.data);
    } catch (error) {
      toast.error('Raporlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchProductUsage = async () => {
    try {
      const params = {};
      if (usageStartDate) params.start_date = usageStartDate;
      if (usageEndDate) params.end_date = usageEndDate;
      
      const res = await api.get('/reports/product-usage', { params });
      setProductUsage(res.data);
    } catch (error) {
      toast.error('Ürün kullanım raporu yüklenirken hata oluştu');
    }
  };

  useEffect(() => {
    fetchProductUsage();
  }, []);

  const calculateTotals = (data) => {
    return data.reduce(
      (acc, item) => ({
        revenue: acc.revenue + item.total_revenue,
        cost: acc.cost + item.total_cost,
        profit: acc.profit + item.profit,
        count: acc.count + item.sales_count,
      }),
      { revenue: 0, cost: 0, profit: 0, count: 0 }
    );
  };

  const dailyTotals = calculateTotals(dailyData);
  const monthlyTotals = calculateTotals(monthlyData);

  const handleExportPDF = async (type) => {
    setExporting(type);
    try {
      let res;
      let filename;
      if (type === 'daily') {
        res = await reportsAPI.exportDailyPDF(parseInt(period));
        filename = `gunluk_rapor_${new Date().toISOString().slice(0, 10)}.pdf`;
      } else if (type === 'inventory') {
        res = await reportsAPI.exportInventoryPDF('all');
        filename = `stok_rapor_${new Date().toISOString().slice(0, 10)}.pdf`;
      }
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF indirildi');
    } catch (error) {
      toast.error('PDF oluşturulamadı');
    } finally {
      setExporting(null);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">
              Raporlar
            </h1>
            <p className="mt-2 text-muted-foreground">
              Finansal analizler ve stok durumu
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40 h-11" data-testid="period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Son 7 Gün</SelectItem>
                <SelectItem value="30">Son 30 Gün</SelectItem>
                <SelectItem value="90">Son 90 Gün</SelectItem>
                <SelectItem value="365">Son 1 Yıl</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => handleExportPDF('daily')}
              disabled={exporting === 'daily'}
              data-testid="export-daily-pdf"
            >
              <FilePdf size={18} className="mr-2" />
              {exporting === 'daily' ? 'İndiriliyor...' : 'Kar/Zarar PDF'}
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExportPDF('inventory')}
              disabled={exporting === 'inventory'}
              data-testid="export-inventory-pdf"
            >
              <Download size={18} className="mr-2" />
              {exporting === 'inventory' ? 'İndiriliyor...' : 'Stok PDF'}
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 stagger-children">
          <div className="border border-border bg-card p-5 rounded-xl">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Toplam Gelir</p>
            <p className="mt-2 text-2xl font-heading font-bold font-data">
              {formatCurrency(dailyTotals.revenue)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Son {period} gün</p>
          </div>
          <div className="border border-border bg-card p-5 rounded-xl">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Toplam Maliyet</p>
            <p className="mt-2 text-2xl font-heading font-bold font-data">
              {formatCurrency(dailyTotals.cost)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Son {period} gün</p>
          </div>
          <div className="border border-border bg-card p-5 rounded-xl">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Toplam Kar</p>
            <p className={`mt-2 text-2xl font-heading font-bold font-data ${dailyTotals.profit >= 0 ? 'text-secondary' : 'text-destructive'}`}>
              {formatCurrency(dailyTotals.profit)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Son {period} gün</p>
          </div>
          <div className="border border-border bg-card p-5 rounded-xl">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Satış Adedi</p>
            <p className="mt-2 text-2xl font-heading font-bold font-data">
              {dailyTotals.count}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Son {period} gün</p>
          </div>
        </div>

        {/* Charts Tabs */}
        <Tabs defaultValue="daily" className="space-y-6">
          <TabsList>
            <TabsTrigger value="daily" className="flex items-center gap-2" data-testid="tab-daily-report">
              <CalendarBlank size={18} />
              Günlük
            </TabsTrigger>
            <TabsTrigger value="monthly" className="flex items-center gap-2" data-testid="tab-monthly-report">
              <ChartLine size={18} />
              Aylık
            </TabsTrigger>
            <TabsTrigger value="cost" className="flex items-center gap-2" data-testid="tab-cost-report">
              <Package size={18} />
              Aylık Maliyet
            </TabsTrigger>
            <TabsTrigger value="usage" className="flex items-center gap-2" data-testid="tab-usage-report">
              <Fire size={18} />
              Ürün Kullanımı
            </TabsTrigger>
          </TabsList>

          {/* Aylık Maliyet Raporu Tab */}
          <TabsContent value="cost" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <CardTitle>Aylık Maliyet Raporu</CardTitle>
                  <div className="flex items-center gap-2">
                    <Label>Ay:</Label>
                    <Input
                      type="month"
                      value={costReportMonth}
                      onChange={(e) => setCostReportMonth(e.target.value)}
                      className="w-40"
                      data-testid="cost-report-month"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingCostReport ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : monthlyCostReport && (
                  <div className="space-y-6">
                    {/* Özet Kartları */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="p-4 rounded-lg border bg-red-50 border-red-200">
                        <p className="text-xs text-red-600 uppercase font-medium">Toplam Gider</p>
                        <p className="text-2xl font-bold text-red-700">{formatCurrency(monthlyCostReport.totalExpenses)}</p>
                      </div>
                      <div className="p-4 rounded-lg border bg-blue-50 border-blue-200">
                        <p className="text-xs text-blue-600 uppercase font-medium">Sevkiyat Maliyeti</p>
                        <p className="text-2xl font-bold text-blue-700">{formatCurrency(monthlyCostReport.transferCost)}</p>
                        <p className="text-xs text-blue-500">{monthlyCostReport.transferCount} sevkiyat</p>
                      </div>
                      <div className="p-4 rounded-lg border bg-purple-50 border-purple-200">
                        <p className="text-xs text-purple-600 uppercase font-medium">Genel Toplam</p>
                        <p className="text-2xl font-bold text-purple-700">{formatCurrency(monthlyCostReport.grandTotal)}</p>
                      </div>
                      <div className="p-4 rounded-lg border bg-green-50 border-green-200">
                        <p className="text-xs text-green-600 uppercase font-medium">Günlük Ortalama</p>
                        <p className="text-2xl font-bold text-green-700">{formatCurrency(monthlyCostReport.grandTotal / 30)}</p>
                      </div>
                    </div>

                    {/* Gider Kategorileri */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Gider Kalemleri</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Kategori</TableHead>
                                <TableHead className="text-center">Adet</TableHead>
                                <TableHead className="text-right">Tutar</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {Object.entries(monthlyCostReport.expenses).map(([cat, data]) => (
                                <TableRow key={cat}>
                                  <TableCell className="font-medium">{cat}</TableCell>
                                  <TableCell className="text-center">{data.count}</TableCell>
                                  <TableCell className="text-right font-bold text-red-600">
                                    {formatCurrency(data.amount)}
                                  </TableCell>
                                </TableRow>
                              ))}
                              <TableRow className="bg-muted/50 font-bold">
                                <TableCell colSpan={2}>TOPLAM GİDER</TableCell>
                                <TableCell className="text-right text-red-700">
                                  {formatCurrency(monthlyCostReport.totalExpenses)}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Sevkiyat Maliyeti (Kategorilere Göre)</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {monthlyCostReport.categoryBreakdown.length === 0 ? (
                            <div className="text-center py-4 text-muted-foreground">
                              Bu ay sevkiyat verisi yok
                            </div>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Kategori</TableHead>
                                  <TableHead className="text-center">Sevkiyat</TableHead>
                                  <TableHead className="text-right">Maliyet</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {monthlyCostReport.categoryBreakdown.map((cat, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell className="font-medium">{cat.category}</TableCell>
                                    <TableCell className="text-center">{cat.count}</TableCell>
                                    <TableCell className="text-right font-bold text-blue-600">
                                      {formatCurrency(cat.cost)}
                                    </TableCell>
                                  </TableRow>
                                ))}
                                <TableRow className="bg-muted/50 font-bold">
                                  <TableCell colSpan={2}>TOPLAM SEVKİYAT</TableCell>
                                  <TableCell className="text-right text-blue-700">
                                    {formatCurrency(monthlyCostReport.transferCost)}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          )}
                        </CardContent>
                      </Card>
                    </div>

                    {/* Genel Toplam Özeti */}
                    <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
                      <CardContent className="pt-6">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                          <div>
                            <h3 className="text-lg font-bold text-purple-800">
                              {new Date(costReportMonth + '-01').toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })} TOPLAM MALİYET
                            </h3>
                            <p className="text-sm text-purple-600">Gider + Sevkiyat Maliyeti</p>
                          </div>
                          <div className="text-right">
                            <p className="text-3xl font-bold text-purple-800">
                              {formatCurrency(monthlyCostReport.grandTotal)}
                            </p>
                            <p className="text-sm text-purple-600">
                              Günlük ort: {formatCurrency(monthlyCostReport.grandTotal / 30)}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="daily" className="space-y-6">
            {/* Daily Chart */}
            <div className="border border-border bg-card p-6 rounded-xl" data-testid="daily-chart">
              <h2 className="font-heading text-xl font-semibold mb-6">Günlük Kar/Zarar</h2>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value) => value.slice(5)}
                      stroke="hsl(var(--muted-foreground))"
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                      stroke="hsl(var(--muted-foreground))"
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                      formatter={(value, name) => [
                        formatCurrency(value),
                        name === 'total_revenue' ? 'Gelir' : name === 'total_cost' ? 'Maliyet' : 'Kar',
                      ]}
                      labelFormatter={(label) => `Tarih: ${label}`}
                    />
                    <Legend
                      formatter={(value) =>
                        value === 'total_revenue' ? 'Gelir' : value === 'total_cost' ? 'Maliyet' : 'Kar'
                      }
                    />
                    <Line
                      type="monotone"
                      dataKey="total_revenue"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="profit"
                      stroke="hsl(var(--secondary))"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Daily Table */}
            <div className="border border-border bg-card p-6 rounded-xl" data-testid="daily-table">
              <h2 className="font-heading text-xl font-semibold mb-4">Günlük Detay (Giderler Dahil)</h2>
              <div className="overflow-auto max-h-96">
                <Table className="data-table">
                  <TableHeader>
                    <TableRow className="border-b border-border">
                      <TableHead className="py-3">Tarih</TableHead>
                      <TableHead className="text-right">Satış</TableHead>
                      <TableHead className="text-right">Gelir</TableHead>
                      <TableHead className="text-right">Maliyet</TableHead>
                      <TableHead className="text-right">Personel</TableHead>
                      <TableHead className="text-right">İşletme</TableHead>
                      <TableHead className="text-right">Brüt Kar</TableHead>
                      <TableHead className="text-right">Net Kar</TableHead>
                      <TableHead className="text-right">Marj</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dailyData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                          Bu dönemde veri bulunmuyor
                        </TableCell>
                      </TableRow>
                    ) : (
                      [...dailyData].reverse().map((row) => (
                        <TableRow key={row.date} className="table-row-hover border-b border-border/40">
                          <TableCell className="py-3 font-medium">{row.date}</TableCell>
                          <TableCell className="text-right font-data">{row.sales_count}</TableCell>
                          <TableCell className="text-right font-data">{formatCurrency(row.total_revenue)}</TableCell>
                          <TableCell className="text-right font-data text-muted-foreground">{formatCurrency(row.total_cost)}</TableCell>
                          <TableCell className="text-right font-data text-orange-500">{formatCurrency(row.staff_cost || 0)}</TableCell>
                          <TableCell className="text-right font-data text-blue-500">{formatCurrency(row.daily_expenses || 0)}</TableCell>
                          <TableCell className={`text-right font-data ${row.profit >= 0 ? 'text-secondary' : 'text-destructive'}`}>
                            {formatCurrency(row.profit)}
                          </TableCell>
                          <TableCell className={`text-right font-data font-bold ${(row.net_profit || row.profit) >= 0 ? 'text-green-600' : 'text-destructive'}`}>
                            {formatCurrency(row.net_profit || row.profit)}
                          </TableCell>
                          <TableCell className={`text-right font-data text-xs ${(row.profit_margin || 0) >= 0 ? 'text-green-600' : 'text-destructive'}`}>
                            {row.profit_margin || 0}%
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="monthly" className="space-y-6">
            {/* Monthly Chart */}
            <div className="border border-border bg-card p-6 rounded-xl" data-testid="monthly-chart">
              <h2 className="font-heading text-xl font-semibold mb-6">Aylık Kar/Zarar</h2>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 11 }}
                      stroke="hsl(var(--muted-foreground))"
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                      stroke="hsl(var(--muted-foreground))"
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                      formatter={(value, name) => [
                        formatCurrency(value),
                        name === 'total_revenue' ? 'Gelir' : name === 'profit' ? 'Kar' : name,
                      ]}
                    />
                    <Legend
                      formatter={(value) =>
                        value === 'total_revenue' ? 'Gelir' : value === 'profit' ? 'Kar' : value
                      }
                    />
                    <Bar dataKey="total_revenue" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="profit" fill="hsl(var(--secondary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Monthly Table */}
            <div className="border border-border bg-card p-6 rounded-xl" data-testid="monthly-table">
              <h2 className="font-heading text-xl font-semibold mb-4">Aylık Detay (Giderler Dahil)</h2>
              <Table className="data-table">
                <TableHeader>
                  <TableRow className="border-b border-border">
                    <TableHead className="py-3">Ay</TableHead>
                    <TableHead className="text-right">Satış</TableHead>
                    <TableHead className="text-right">Gelir</TableHead>
                    <TableHead className="text-right">Maliyet</TableHead>
                    <TableHead className="text-right">Giderler</TableHead>
                    <TableHead className="text-right">Brüt Kar</TableHead>
                    <TableHead className="text-right">Net Kar</TableHead>
                    <TableHead className="text-right">Marj</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {monthlyData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        Bu dönemde veri bulunmuyor
                      </TableCell>
                    </TableRow>
                  ) : (
                    [...monthlyData].reverse().map((row) => (
                      <TableRow key={row.month} className="table-row-hover border-b border-border/40">
                        <TableCell className="py-3 font-medium">{row.month}</TableCell>
                        <TableCell className="text-right font-data">{row.sales_count}</TableCell>
                        <TableCell className="text-right font-data">{formatCurrency(row.total_revenue)}</TableCell>
                        <TableCell className="text-right font-data text-muted-foreground">{formatCurrency(row.total_cost)}</TableCell>
                        <TableCell className="text-right font-data text-blue-500">{formatCurrency(row.expenses || 0)}</TableCell>
                        <TableCell className={`text-right font-data ${row.profit >= 0 ? 'text-secondary' : 'text-destructive'}`}>
                          {formatCurrency(row.profit)}
                        </TableCell>
                        <TableCell className={`text-right font-data font-bold ${(row.net_profit || row.profit) >= 0 ? 'text-green-600' : 'text-destructive'}`}>
                          {formatCurrency(row.net_profit || row.profit)}
                        </TableCell>
                        <TableCell className={`text-right font-data text-xs ${(row.profit_margin || 0) >= 0 ? 'text-green-600' : 'text-destructive'}`}>
                          {row.profit_margin || 0}%
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="usage" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <CardTitle className="flex items-center gap-2">
                    <Fire size={24} />
                    En Çok Kullanılan Ürünler
                  </CardTitle>
                  <div className="flex gap-2 items-end flex-wrap">
                    <div>
                      <Label className="text-xs">Başlangıç</Label>
                      <Input
                        type="date"
                        value={usageStartDate}
                        onChange={(e) => setUsageStartDate(e.target.value)}
                        className="w-36"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Bitiş</Label>
                      <Input
                        type="date"
                        value={usageEndDate}
                        onChange={(e) => setUsageEndDate(e.target.value)}
                        className="w-36"
                      />
                    </div>
                    <Button onClick={fetchProductUsage} data-testid="filter-usage-btn">
                      Filtrele
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {productUsage && (
                  <>
                    {/* Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Dönem</p>
                        <p className="font-semibold">{productUsage.start_date} - {productUsage.end_date}</p>
                      </div>
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Toplam Ürün</p>
                        <p className="text-xl font-bold">{productUsage.total_products}</p>
                      </div>
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Toplam Kullanım Maliyeti</p>
                        <p className="text-xl font-bold">{formatCurrency(productUsage.total_usage_cost)}</p>
                      </div>
                      <div className="p-4 bg-primary/10 rounded-lg">
                        <p className="text-xs text-muted-foreground">Fire Dahil Net Maliyet</p>
                        <p className="text-xl font-bold text-primary">{formatCurrency(productUsage.net_total_usage_cost)}</p>
                      </div>
                    </div>

                    {/* Bar Chart - Top 10 */}
                    {productUsage.products.length > 0 && (
                      <div className="mb-6">
                        <h3 className="font-semibold mb-4">En Çok Kullanılan 10 Ürün</h3>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={productUsage.products.slice(0, 10)} layout="vertical">
                              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                              <XAxis type="number" tick={{ fontSize: 11 }} />
                              <YAxis 
                                dataKey="product_name" 
                                type="category" 
                                width={120}
                                tick={{ fontSize: 11 }}
                              />
                              <Tooltip 
                                formatter={(value, name) => [
                                  `${formatNumber(value)}`,
                                  name === 'total_quantity' ? 'Toplam Kullanım' : name
                                ]}
                                contentStyle={{
                                  backgroundColor: 'hsl(var(--card))',
                                  border: '1px solid hsl(var(--border))',
                                  borderRadius: '8px',
                                }}
                              />
                              <Bar dataKey="total_quantity" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {/* Table */}
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">#</TableHead>
                          <TableHead>Ürün</TableHead>
                          <TableHead>Kategori</TableHead>
                          <TableHead className="text-right">Transfer</TableHead>
                          <TableHead className="text-right">Çıkış</TableHead>
                          <TableHead className="text-right">Toplam</TableHead>
                          <TableHead className="text-right">Birim Fiyat</TableHead>
                          <TableHead className="text-right">Net Fiyat (Fire)</TableHead>
                          <TableHead className="text-right">Toplam Maliyet</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {productUsage.products.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                              Bu dönemde ürün kullanımı bulunamadı
                            </TableCell>
                          </TableRow>
                        ) : (
                          productUsage.products.map((item, index) => (
                            <TableRow key={item.product_id} data-testid={`usage-row-${item.product_id}`}>
                              <TableCell className="font-bold text-muted-foreground">{index + 1}</TableCell>
                              <TableCell className="font-medium">{item.product_name}</TableCell>
                              <TableCell className="text-muted-foreground">{item.category}</TableCell>
                              <TableCell className="text-right">{formatNumber(item.transfer_quantity)} {item.unit}</TableCell>
                              <TableCell className="text-right">{formatNumber(item.exit_quantity)} {item.unit}</TableCell>
                              <TableCell className="text-right font-semibold">{formatNumber(item.total_quantity)} {item.unit}</TableCell>
                              <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                              <TableCell className="text-right text-primary font-medium">
                                {item.net_unit_price > item.unit_price ? formatCurrency(item.net_unit_price) : '-'}
                              </TableCell>
                              <TableCell className="text-right font-bold">{formatCurrency(item.net_total_cost)}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Bottom Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Ingredients */}
          <div className="border border-border bg-card p-6 rounded-xl" data-testid="top-ingredients-report">
            <h2 className="font-heading text-xl font-semibold mb-4">En Çok Kullanılan Malzemeler</h2>
            <Table className="data-table">
              <TableHeader>
                <TableRow className="border-b border-border">
                  <TableHead className="py-3">#</TableHead>
                  <TableHead>Malzeme</TableHead>
                  <TableHead className="text-right">Kullanım</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topIngredients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                      Henüz veri bulunmuyor
                    </TableCell>
                  </TableRow>
                ) : (
                  topIngredients.map((item, index) => (
                    <TableRow key={item.product_id} className="table-row-hover border-b border-border/40">
                      <TableCell className="py-3">
                        <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
                          {index + 1}
                        </span>
                      </TableCell>
                      <TableCell className="font-medium">{item.product_name}</TableCell>
                      <TableCell className="text-right font-data">
                        {formatNumber(item.total_used)} {item.unit}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Low Stock */}
          <div className="border border-border bg-card p-6 rounded-xl" data-testid="low-stock-report">
            <h2 className="font-heading text-xl font-semibold mb-4">Düşük Stok Uyarıları</h2>
            <Table className="data-table">
              <TableHeader>
                <TableRow className="border-b border-border">
                  <TableHead className="py-3">Ürün</TableHead>
                  <TableHead className="text-right">Mevcut</TableHead>
                  <TableHead className="text-right">Minimum</TableHead>
                  <TableHead className="text-center">Durum</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lowStock.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      Tüm stoklar yeterli seviyede
                    </TableCell>
                  </TableRow>
                ) : (
                  lowStock.map((item) => (
                    <TableRow key={item.id} className="table-row-hover border-b border-border/40">
                      <TableCell className="py-3 font-medium">{item.name}</TableCell>
                      <TableCell className="text-right font-data">
                        {formatNumber(item.total_stock)} {item.unit}
                      </TableCell>
                      <TableCell className="text-right font-data text-muted-foreground">
                        {formatNumber(item.min_stock_level)} {item.unit}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={`badge-pill ${item.status === 'critical' ? 'status-critical' : 'status-low'}`}>
                          {item.status === 'critical' ? 'Kritik' : 'Düşük'}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
